import Immutable from "immutable";
import { spreadOutDataWithCommasArr } from "util/spreadcommas";
import { extent } from "d3-array";

import moment from "moment/moment";
import * as types from "./constants";

const initialState = Immutable.fromJS({
  isFetching: false,
  didInvalidate: false,
  page: 1,
  itemsPerPage: 10,
  casts: [],
  deviceModels: [],
  deviceSn: [],
  surveyStationOptions: [],
  surveyFilter: [],
  stationFilter: [],
  downloadType: "xlsx",
  processingStageFilter: "8_binAvg",
  instrumentFilter: "any",
  instrumentFilterOverride: "",
  serialNumFilter: "any",
  workareaFilter: [],
  organizationFilter: [],
  bufferFilter: false,
  upcastFilter: false,
  filenameFilter: [],
  castPkFilter: [],
  hakaiIdFilter: [],
  totalCastsWithFilters: 0,
  bulkDownloadIsFetching: false,
  metadataDownloadIsFetching: false,
  previewFileDialogOpen: false,
  faqModalOpen: false,
  castDistChartModalOpen: false,
  castDistChartDataIsFetching: false,
  castDistChartData: [],
  brushedCastDistChartData: [],
  brushExtent: [],
  brushDidInvalidate: false,
  previewFileTitle: "",
  previewFileBody: "",
  surveyStationOptionsIsFetching: false
});

function onlyArrayToList(toConvert) {
  if (Array.isArray(toConvert)) {
    return Immutable.fromJS(toConvert);
  }
  return Immutable.List();
}

function handleAction(state, action) {
  switch (action.type) {
    case types.SIGN_OUT_COMPLETE:
      return initialState;
    case types.WORKAREA_SET:
      return initialState;

    case types.DOWNLOAD_CASTS_INVALIDATE:
      return state.merge({
        didInvalidate: true,
        page: 1,
        casts: Immutable.List()
      });

    case types.DOWNLOAD_CASTS_FETCH_REQUEST:
      return state.merge({
        didInvalidate: false,
        isFetching: true
      });

    case types.DOWNLOAD_CASTS_FETCH_SUCCESS:
      return state.withMutations(s =>
        s
          .set("didInvalidate", false)
          .set("isFetching", false)
          .set("lastUpdated", action.meta.receivedAt)
          .update("casts", casts =>
            casts.concat(
              Immutable.fromJS(
                action.payload.casts.map(f => ({
                  ctd_cast_pk: f.ctd_cast_pk,
                  ctd_file_pk: f.ctd_file_pk,
                  selected: state.get("processingStageFilter"),
                  name: f.filename,
                  data: f,
                  contentIsFetching: false,
                  pdfContentIsFetching: false,
                  contentDidInvalidate: false,
                  isWirewalker: f.is_wirewalker
                }))
              )
            )
          )
      );
    case types.DOWNLOAD_DEVICE_INFO_REQUEST:
      return state.merge({
        isFetching: true
      });

    case types.DOWNLOAD_DEVICE_INFO_SUCCESS:
      return state.withMutations(s => {
        s.merge({
          isFetching: false,
          deviceModels: action.payload.deviceInfo.deviceModels,
          deviceSn: action.payload.deviceInfo.deviceSn
        });
      });

    case types.DOWNLOAD_CASTS_CHANGE_SELECTED:
      return state.update("casts", casts =>
        casts.map(f => {
          if (
            f.get("ctd_cast_pk") === action.payload.ctd_cast_pk &&
            action.payload.selected !== f.get("selected")
          ) {
            return f.merge({
              selected: action.payload.selected,
              contentDidInvalidate: true
            });
          }
          return f;
        })
      );

    case types.DOWNLOAD_FILE_FETCH_CONTENT_REQUEST:
      return state.update("casts", casts =>
        casts.map(f => {
          if (f.get("ctd_cast_pk") === action.payload) {
            return f.set("contentIsFetching", true);
          }
          return f;
        })
      );

    case types.DOWNLOAD_FILE_FETCH_CONTENT_SUCCESS:
      return state.update("casts", casts =>
        casts.map(f => {
          if (f.get("ctd_cast_pk") === action.payload.ctd_cast_pk) {
            return f.merge({
              contentIsFetching: false,
              contentDidInvalidate: false,
              content: action.payload.content,
              contentType: action.payload.contentType
            });
          }
          return f;
        })
      );

    case types.DOWNLOAD_FILE_FETCH_CONTENT_ERROR:
      return state.update("casts", casts =>
        casts.map(f => {
          if (f.get("ctd_cast_pk") === action.meta.ctd_cast_pk) {
            return f.merge({
              contentIsFetching: false,
              pdfContentIsFetching: false,
              contentDidInvalidate: true,
              content: undefined
            });
          }
          return f;
        })
      );

    case types.DOWNLOAD_PROCESS_PDF_FILE_FETCH_CONTENT_REQUEST:
      return state.update("casts", casts =>
        casts.map(f => {
          if (f.get("ctd_cast_pk") === action.payload) {
            return f.set("pdfContentIsFetching", true);
          }
          return f;
        })
      );

    case types.DOWNLOAD_PROCESS_PDF_FILE_FETCH_CONTENT_SUCCESS:
      return state.update("casts", casts =>
        casts.map(f => {
          if (f.get("ctd_cast_pk") === action.payload.ctd_cast_pk) {
            return f.merge({
              pdfContentIsFetching: false,
              contentDidInvalidate: false,
              content: action.payload.content,
              contentType: action.payload.contentType
            });
          }
          return f;
        })
      );

    case types.DOWNLOAD_CASTS_SET_PAGE_NUMBER:
      return state.set("page", action.payload);

    case types.DOWNLOAD_CASTS_SET_ITEMS_PER_PAGE:
      return state.merge({
        page: 1,
        itemsPerPage: Immutable.fromJS(action.payload)
      });

    case types.SET_PROCESSING_STAGE_FILTER:
      return state.set(
        "processingStageFilter",
        Immutable.fromJS(action.payload)
      );

    case types.SET_INSTRUMENT_FILTER:
      return state.set("instrumentFilter", Immutable.fromJS(action.payload));

    case types.SET_INSTRUMENT_FILTER_OVERRIDE:
      return state.set(
        "instrumentFilterOverride",
        Immutable.fromJS(action.payload)
      );

    case types.SET_SERIAL_NUM_FILTER:
      return state.set("serialNumFilter", Immutable.fromJS(action.payload));

    case types.SET_WORKAREA_FILTER:
      return state.set("workareaFilter", Immutable.fromJS(action.payload));

    case types.SET_ORGANIZATION_FILTER:
      return state.set("organizationFilter", Immutable.fromJS(action.payload));

    case types.SET_BUFFER_FILTER:
      return state.set("bufferFilter", action.payload);

    case types.SET_UPCAST_FILTER:
      return state.set("upcastFilter", action.payload);

    case types.SET_FILENAME_FILTER:
      return state.set("filenameFilter", Immutable.fromJS(action.payload));

    case types.SET_CAST_PK_FILTER:
      return state.set("castPkFilter", Immutable.fromJS(action.payload));

    case types.SET_HAKAI_ID_FILTER:
      return state.set("hakaiIdFilter", Immutable.fromJS(action.payload));

    case types.SET_SURVEY_FILTER:
      return state.set("surveyFilter", Immutable.fromJS(action.payload));

    case types.SET_SURVEY_STATION_OPTIONS: {
      // remove null options, separate comma separated surveys eg "QCS_V1,KWY_V2" into individual options
      let filteredPayload = action.payload;
      if (Array.isArray(filteredPayload)) {
        filteredPayload = spreadOutDataWithCommasArr(
          action.payload.filter(x => x.cruise !== null)
        );
      }
      return state.merge({
        surveyStationOptions: onlyArrayToList(filteredPayload),
        surveyStationOptionsIsFetching: false
      });
    }

    case types.SET_STATION_FILTER:
      return state.set("stationFilter", Immutable.fromJS(action.payload));

    case types.SET_START_DATE_FILTER:
      return state.set("startDateFilter", Immutable.fromJS(action.payload));

    case types.SET_END_DATE_FILTER:
      return state.set("endDateFilter", Immutable.fromJS(action.payload));

    case types.SET_TOTAL_CASTS_WITH_FILTERS:
      return state.set(
        "totalCastsWithFilters",
        Immutable.fromJS(action.payload)
      );
    case types.SET_TOTAL_CASTS_WITH_FILTERS_ERROR:
      return state.merge({
        didInvalidate: true,
        isFetching: false
      });

    case types.SET_SURVEY_STATION_OPTIONS_FETCH:
      return state.set("surveyStationOptionsIsFetching", true);

    case types.ZIP_DOWNLOAD_FETCH_REQUEST:
    case types.XLSX_DOWNLOAD_FETCH_REQUEST:
    case types.CSV_DOWNLOAD_FETCH_REQUEST:
      return state.set("bulkDownloadIsFetching", true);

    case types.CSV_DOWNLOAD_FETCH_SUCCESS:
    case types.XLSX_DOWNLOAD_FETCH_SUCCESS:
    case types.ZIP_DOWNLOAD_FETCH_SUCCESS:
    case types.CSV_DOWNLOAD_FETCH_ERROR:
    case types.XLSX_DOWNLOAD_FETCH_ERROR:
    case types.ZIP_DOWNLOAD_FETCH_ERROR:
      return state.set("bulkDownloadIsFetching", false);

    case types.XLSX_DOWNLOAD_METADATA_FETCH_REQUEST:
    case types.CSV_DOWNLOAD_METADATA_FETCH_REQUEST:
      return state.set("metadataDownloadIsFetching", true);

    case types.CSV_DOWNLOAD_METADATA_FETCH_SUCCESS:
    case types.XLSX_DOWNLOAD_METADATA_FETCH_SUCCESS:
    case types.CSV_DOWNLOAD_METADATA_FETCH_ERROR:
    case types.XLSX_DOWNLOAD_METADATA_FETCH_ERROR:
      return state.set("metadataDownloadIsFetching", false);

    case types.PREVIEW_FILE_DIALOG_OPEN:
      return state.set("previewFileDialogOpen", true);

    case types.PREVIEW_FILE_DIALOG_CLOSE:
      return state.set("previewFileDialogOpen", false);

    case types.FAQ_MODAL_OPEN:
      return state.set("faqModalOpen", true);

    case types.FAQ_MODAL_CLOSE:
      return state.set("faqModalOpen", false);

    case types.CAST_DIST_CHART_MODAL_OPEN:
      return state.set("castDistChartModalOpen", true);

    case types.CAST_DIST_CHART_MODAL_CLOSE:
      return state.merge({
        castDistChartModalOpen: false,
        castDistChartData: [],
        brushExtent: []
      });

    case types.SET_BRUSH_EXTENT:
      return state.merge({
        brushExtent: action.payload
      });

    case types.CLEAR_BRUSH:
      return state.set("brushDidInvalidate", true);

    case types.BRUSH_DID_CLEAR:
      return state.set("brushDidInvalidate", false);

    case types.CAST_DIST_CHART_DATA_FETCH_REQUEST:
      return state.set("castDistChartDataIsFetching", true);

    case types.CAST_DIST_CHART_DATA_FETCH_SUCCESS:
      return state.mergeDeep({
        castDistChartDataIsFetching: false,
        castDistChartData: action.payload,
        brushExtent: extent(action.payload, d => d.time) // initialize brush to full dataset
      });

    case types.CAST_DIST_CHART_DATA_FETCH_ERROR:
      return state.merge({
        castDistChartDataIsFetching: false
      });

    case types.PREVIEW_FILE_SET_TITLE:
      return state.set("previewFileTitle", action.payload);

    case types.PREVIEW_FILE_SET_BODY:
      return state.set("previewFileBody", action.payload);

    case types.SET_CTD_DOWNLOAD_TYPE:
      return state.set("downloadType", action.payload);

    case types.BULK_UPDATE_DOWNLOAD_FILTERS: {
      let updatedState = state;
      // eslint-disable-next-line no-restricted-syntax
      for (const [key, val] of Object.entries(action.payload)) {
        updatedState = updatedState.set(key, val);
      }
      return updatedState;
    }

    default:
      return state;
  }
}

function fieldNotNull(value) {
  if (typeof value === "undefined" || !value) {
    return false;
  }
  if (value instanceof moment || typeof value === "boolean") {
    return true;
  }
  return value.size > 0 && value !== "any";
}

export function updateUrl(state) {
  const paramObj = new URLSearchParams();
  // eslint-disable-next-line no-restricted-syntax
  for (const k of Array.from(state.keys()).filter(
    key => key.endsWith("Filter") && fieldNotNull(state.get(key))
  )) {
    if (!(state.get(k) instanceof moment) && state.get(k) instanceof Object) {
      paramObj.append(k, Array.from(state.get(k)));
    } else if (state.get(k) instanceof moment) {
      paramObj.append(k, state.get(k).format("YYYY-MM-DD"));
    } else {
      paramObj.append(k, state.get(k));
    }
  }
  const baseUrl = window.location.href.split("?")[0];
  const newUrl = `${baseUrl}?${paramObj.toString()}`;
  if (newUrl !== window.location.href) {
    window.history.pushState({}, "", `${baseUrl}?${paramObj.toString()}`);
  }
}

function downloadReducer(state = initialState, action) {
  const updatedState = handleAction(state, action);
  if (state !== updatedState) {
    updateUrl(updatedState);
  }
  return updatedState;
}

export default downloadReducer;
